import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Users,
  Package,
  CreditCard,
  BarChart3,
  Settings,
  UserCog,
  Shield,
  ClipboardList,
  ChevronDown,
  ChevronRight,
  X,
  User as UserIcon,
  Palette,
  SlidersHorizontal,
} from 'lucide-react';
import { useAuthContext } from '@/contexts/AuthContext';
import type { Permission } from '@/types';
import { clsx } from 'clsx';

interface NavChild {
  label: string;
  to: string;
  permission?: Permission;
}

interface NavItem {
  icon: React.ReactNode;
  label: string;
  to?: string;
  permissions?: Permission[];
  children?: NavChild[];
}

interface NavGroup {
  section?: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    items: [
      { icon: <LayoutDashboard size={18} />, label: 'Dashboard', to: '/dashboard' },
    ],
  },
  {
    section: 'Transactions',
    items: [
      {
        icon: <FileText size={18} />,
        label: 'Estimates',
        to: '/invoices',
        permissions: ['Invoice.View', 'Invoice.Create'],
      },
      {
        icon: <CreditCard size={18} />,
        label: 'Payments',
        to: '/payments',
        permissions: ['Payment.View', 'Payment.Create'],
      },
    ],
  },
  {
    section: 'Master Data',
    items: [
      {
        icon: <Users size={18} />,
        label: 'Customers',
        to: '/customers',
        permissions: ['Customer.View', 'Customer.Create'],
      },
      {
        icon: <Package size={18} />,
        label: 'Products',
        to: '/products',
        permissions: ['Product.View', 'Product.Create'],
      },
      {
        icon: <Palette size={18} />,
        label: 'Rate List',
        to: '/rate-list',
      },
      {
        icon: <SlidersHorizontal size={18} />,
        label: 'Manage Rates',
        to: '/rate-list/manage',
        permissions: ['Product.Edit', 'Product.Create', 'RateList.Manage'],
      },
    ],
  },
  {
    section: 'Analytics',
    items: [
      {
        icon: <BarChart3 size={18} />,
        label: 'Reports',
        permissions: ['Report.View'],
        children: [
          { label: 'Sales Report', to: '/reports/sales', permission: 'Report.View' },
          { label: 'GST Report', to: '/reports/gst', permission: 'Report.View' },
          { label: 'Customer Report', to: '/reports/customers', permission: 'Report.View' },
          { label: 'Product Report', to: '/reports/products', permission: 'Report.View' },
          { label: 'Payment Report', to: '/reports/payments', permission: 'Report.View' },
        ],
      },
    ],
  },
  {
    section: 'Administration',
    items: [
      {
        icon: <UserCog size={18} />,
        label: 'Users',
        to: '/users',
        permissions: ['User.Manage'],
      },
      {
        icon: <Shield size={18} />,
        label: 'Roles',
        to: '/roles',
        permissions: ['Role.Manage'],
      },
      {
        icon: <ClipboardList size={18} />,
        label: 'Audit Logs',
        to: '/audit-logs',
        permissions: ['AuditLog.View'],
      },
      {
        icon: <Settings size={18} />,
        label: 'Settings',
        children: [
          { label: 'Company', to: '/settings/company', permission: 'Settings.Manage' },
          { label: 'Estimate', to: '/settings/invoice', permission: 'Settings.Manage' },
          { label: 'Tax', to: '/settings/tax', permission: 'Settings.Manage' },
          { label: 'Units', to: '/settings/units', permission: 'Settings.Manage' },
          { label: 'Templates', to: '/settings/templates', permission: 'Template.Manage' },
          { label: 'Profile', to: '/settings/profile' },
        ],
      },
    ],
  },
];

interface SidebarProps {
  mobile?: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ mobile, onClose }) => {
  const { appUser, firebaseUser, role, can, canAny, isAdmin, company } = useAuthContext();
  const [expanded, setExpanded] = React.useState<string[]>(['Reports', 'Settings']);

  const toggle = (label: string) => {
    setExpanded((p) =>
      p.includes(label) ? p.filter((x) => x !== label) : [...p, label],
    );
  };

  // Filter navigation items by active user permissions
  const filteredGroups = useMemo(() => {
    return navGroups
      .map((group) => {
        const visibleItems = group.items
          .map((item) => {
            if (item.children) {
              const visibleChildren = item.children.filter(
                (c) => !c.permission || isAdmin || can(c.permission),
              );
              if (visibleChildren.length === 0) return null;
              return { ...item, children: visibleChildren };
            }
            if (!item.permissions || isAdmin || canAny(item.permissions)) {
              return item;
            }
            return null;
          })
          .filter((item): item is NavItem => item !== null);

        if (visibleItems.length === 0) return null;
        return { ...group, items: visibleItems };
      })
      .filter((g): g is NavGroup => g !== null);
  }, [isAdmin, can, canAny]);

  const userEmail = firebaseUser?.email || appUser?.email || '';
  const emailName = userEmail
    ? userEmail.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : '';

  const userName = (() => {
    if (appUser?.name && appUser.name.trim() && appUser.name.toLowerCase() !== 'admin') {
      return appUser.name.trim();
    }
    if (firebaseUser?.displayName && firebaseUser.displayName.trim() && firebaseUser.displayName.toLowerCase() !== 'admin') {
      return firebaseUser.displayName.trim();
    }
    if (appUser?.name && appUser.name.trim()) {
      return appUser.name.trim();
    }
    if (firebaseUser?.displayName && firebaseUser.displayName.trim()) {
      return firebaseUser.displayName.trim();
    }
    if (emailName) {
      return emailName;
    }
    return 'User';
  })();
  const initial = userName.charAt(0).toUpperCase();
  const roleName = role?.name || appUser?.roleId || 'Staff';
  const photoUrl = appUser?.photoUrl || firebaseUser?.photoURL;

  return (
    <div
      className={clsx(
        'flex flex-col h-full bg-white border-r border-gray-100',
        mobile ? 'w-72' : 'w-64',
      )}
    >
      {/* Logo & Company Name */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3 min-w-0 flex-1 mr-2">
          {company?.logoUrl ? (
            <img
              src={company.logoUrl}
              alt={company.name || 'Company'}
              className="w-8 h-8 rounded-lg object-contain bg-gray-50 border border-gray-100 flex-shrink-0"
            />
          ) : (
            <img
              src="/favicon.svg"
              alt="UNIBMS"
              className="w-8 h-8 rounded-lg object-contain shadow-xs flex-shrink-0"
            />
          )}
          <div className="min-w-0 flex-1">
            <span
              className="font-bold text-gray-900 text-base tracking-tight truncate block leading-tight"
              title={company?.name || 'UNIBMS'}
            >
              {company?.name || 'UNIBMS'}
            </span>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)] animate-pulse flex-shrink-0" />
              <span className="text-2xs font-semibold text-emerald-600 leading-none truncate">
                {appUser?.companyId ? 'Active' : 'Demo'}
              </span>
            </div>
          </div>
        </div>
        {mobile && (
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 cursor-pointer flex-shrink-0"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {filteredGroups.map((group, gIdx) => (
          <div key={gIdx} className="space-y-0.5">
            {group.section && (
              <p className="sidebar-section">
                {group.section}
              </p>
            )}
            {group.items.map((item, iIdx) => {
              if (item.children) {
                const isOpen = expanded.includes(item.label);
                return (
                  <div key={iIdx}>
                    <button
                      onClick={() => toggle(item.label)}
                      className="sidebar-item w-full justify-between"
                    >
                      <span className="flex items-center gap-3">
                        {item.icon}
                        {item.label}
                      </span>
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                    {isOpen && (
                      <div className="ml-7 mt-0.5 space-y-0.5">
                        {item.children.map((child) => (
                          <NavLink
                            key={child.to}
                            to={child.to}
                            onClick={onClose}
                            className={({ isActive }) =>
                              clsx(
                                'flex items-center px-3 py-2 rounded-lg text-sm transition-colors',
                                isActive
                                  ? 'bg-primary-50 text-primary-700 font-semibold'
                                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700',
                              )
                            }
                          >
                            {child.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={iIdx}
                  to={item.to!}
                  onClick={onClose}
                  className={({ isActive }) =>
                    clsx('sidebar-item', isActive && 'sidebar-item-active')
                  }
                >
                  {item.icon}
                  {item.label}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      {/* User Role Footer Badge */}
      <div className="p-3 border-t border-gray-100 bg-gray-50/70">
        <div className="flex items-center gap-2.5 p-2 rounded-xl bg-white border border-gray-200/80 shadow-2xs">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center font-bold text-xs flex-shrink-0 overflow-hidden">
            {photoUrl ? (
              <img src={photoUrl} alt={userName} className="w-full h-full object-cover" />
            ) : (
              initial
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold text-gray-800 truncate" title={userName}>{userName}</p>
            <div className="flex items-center gap-1 mt-0.5">
              <Shield size={10} className="text-primary-600 flex-shrink-0" />
              <span className="text-2xs font-semibold text-primary-700 uppercase tracking-wide truncate">
                {roleName}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
